import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { X, Upload, User, Lock, Trash2, Crop, Check, CheckCircle2, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import ReactCrop, { centerCrop, makeAspectCrop } from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import api from '../utils/api';

/**
 * Self-edit profile modal — used by tenant user and superadmin.
 * Props:
 *  - kind: 'tenant' | 'superadmin'
 *  - currentUser: { username, nomeCompleto, fotoPerfil }
 *  - onSuccess(updatedUser)
 */

function centerAspectCrop(mediaWidth, mediaHeight, aspect) {
  return centerCrop(
    makeAspectCrop({ unit: '%', width: 90 }, aspect, mediaWidth, mediaHeight),
    mediaWidth,
    mediaHeight,
  );
}

async function getCroppedImg(imgEl, crop) {
  const canvas = document.createElement('canvas');
  const scaleX = imgEl.naturalWidth / imgEl.width;
  const scaleY = imgEl.naturalHeight / imgEl.height;
  const pixelRatio = window.devicePixelRatio || 1;

  const cropX = (crop.x / 100) * imgEl.width;
  const cropY = (crop.y / 100) * imgEl.height;
  const cropW = (crop.width / 100) * imgEl.width;
  const cropH = (crop.height / 100) * imgEl.height;

  canvas.width = Math.floor(cropW * scaleX * pixelRatio);
  canvas.height = Math.floor(cropH * scaleY * pixelRatio);

  const ctx = canvas.getContext('2d');
  ctx.scale(pixelRatio, pixelRatio);
  ctx.imageSmoothingQuality = 'high';

  ctx.drawImage(
    imgEl,
    cropX * scaleX,
    cropY * scaleY,
    cropW * scaleX,
    cropH * scaleY,
    0, 0,
    cropW * scaleX,
    cropH * scaleY,
  );

  return new Promise((resolve) => {
    canvas.toBlob(blob => {
      if (!blob) return;
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.readAsDataURL(blob);
    }, 'image/jpeg', 0.88);
  });
}

export default function ProfileModal({ isOpen, onClose, kind = 'tenant', currentUser, onSuccess }) {
  const isSA = kind === 'superadmin';
  const profileEndpoint = isSA ? '/superadmin/me' : '/auth/me';
  const passwordEndpoint = isSA ? '/superadmin/me/senha' : '/auth/me/senha';

  const [tab, setTab] = useState('perfil');
  const [username, setUsername] = useState('');
  const [nomeCompleto, setNomeCompleto] = useState('');
  const [fotoPerfil, setFotoPerfil] = useState('');

  // Crop state
  const [rawSrc, setRawSrc] = useState('');
  const [crop, setCrop] = useState();
  const [completedCrop, setCompletedCrop] = useState();
  const [showCrop, setShowCrop] = useState(false);
  const imgRef = useRef(null);
  const fileRef = useRef();

  const [senhaAtual, setSenhaAtual] = useState('');
  const [senhaNova, setSenhaNova] = useState('');
  const [senhaConfirm, setSenhaConfirm] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const wasOpenRef = useRef(false);

  useEffect(() => {
    if (isOpen && !wasOpenRef.current) {
      setTab('perfil');
      setUsername(currentUser?.username || '');
      setNomeCompleto(currentUser?.nomeCompleto || '');
      setFotoPerfil(currentUser?.fotoPerfil || '');
      setRawSrc(''); setShowCrop(false); setCrop(undefined); setCompletedCrop(undefined);
      setSenhaAtual(''); setSenhaNova(''); setSenhaConfirm('');
      setError(''); setSuccess('');
    }
    wasOpenRef.current = isOpen;
  }, [isOpen, currentUser]);

  const handlePhoto = (e) => {
    const file = e.target.files[0];
    e.target.value = '';
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      setRawSrc(ev.target.result);
      setShowCrop(true);
      setCrop(undefined);
      setCompletedCrop(undefined);
      setError('');
    };
    reader.readAsDataURL(file);
  };

  const onImageLoad = useCallback((e) => {
    const { width, height } = e.currentTarget;
    setCrop(centerAspectCrop(width, height, 1));
  }, []);

  const handleConfirmCrop = async () => {
    if (!imgRef.current || !completedCrop) return;
    try {
      const base64 = await getCroppedImg(imgRef.current, completedCrop);
      if (base64.length > 800_000) {
        setError('Imagem após corte ainda é grande. Tente uma foto menor.');
        return;
      }
      setFotoPerfil(base64);
      setShowCrop(false);
      setRawSrc('');
    } catch {
      setError('Erro ao processar imagem.');
    }
  };

  const handleCancelCrop = () => {
    setShowCrop(false);
    setRawSrc('');
  };

  const saveProfile = async () => {
    setLoading(true); setError(''); setSuccess('');
    try {
      const payload = { nomeCompleto, fotoPerfil };
      if (isSA && username !== currentUser?.username) payload.username = username;
      const res = await api.put(profileEndpoint, payload);
      const msg = 'Salvo com sucesso!';
      setSuccess(msg);
      toast.success(msg);
      onSuccess?.(res.data);
    } catch (err) {
      const d = err.response?.data?.detail;
      const msg = typeof d === 'string' ? d : 'Erro ao salvar perfil.';
      setError(msg);
      toast.error(msg);
    } finally { setLoading(false); }
  };

  const savePassword = async () => {
    if (!senhaAtual || !senhaNova) {
      const msg = 'Preencha as duas senhas.';
      setError(msg);
      toast.error(msg);
      return;
    }
    if (senhaNova.length < 8) {
      const msg = 'Nova senha precisa de pelo menos 8 caracteres.';
      setError(msg);
      toast.error(msg);
      return;
    }
    if (senhaNova !== senhaConfirm) {
      const msg = 'A confirmação não confere.';
      setError(msg);
      toast.error(msg);
      return;
    }
    setLoading(true); setError(''); setSuccess('');
    try {
      await api.put(passwordEndpoint, { senhaAtual, senhaNova });
      const msg = 'Salvo com sucesso!';
      setSuccess(msg);
      toast.success(msg);
      setSenhaAtual(''); setSenhaNova(''); setSenhaConfirm('');
    } catch (err) {
      const d = err.response?.data?.detail;
      let msg = 'Erro ao alterar senha.';
      if (typeof d === 'string') {
        msg = d;
      } else if (Array.isArray(d) && d.length > 0) {
        msg = d.map(item => item.msg).join(' — ');
      }
      setError(msg);
      toast.error(msg);
    } finally { setLoading(false); }
  };

  if (!isOpen) return null;

  const accent = isSA ? '#0f172a' : 'var(--tenant-cards, #0147a0)';
  const initial = (nomeCompleto || username || 'A')[0]?.toUpperCase();

  return createPortal((
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 overflow-y-auto"
      style={{ background: 'rgba(15,23,42,0.5)', backdropFilter: 'blur(4px)' }}
      onClick={showCrop ? undefined : onClose} data-testid="profile-modal-overlay">
      <div className="nexus-modal w-full max-w-lg flex flex-col"
        style={{ maxHeight: '90vh' }} onClick={e => e.stopPropagation()} data-testid="profile-modal">
        <div className="h-1.5 rounded-t-[28px]" style={{ background: accent }} />
        <div className="flex items-center justify-between px-6 py-3 border-b border-slate-100">
          <h2 className="text-base font-bold text-slate-800" style={{ fontFamily: 'Manrope, sans-serif' }}>
            {showCrop ? 'Ajustar foto' : (isSA ? 'Meu Perfil (Super Admin)' : 'Meu Perfil')}
          </h2>
          {!showCrop && (
            <button onClick={onClose} className="text-slate-400 hover:text-slate-600" data-testid="profile-modal-close">
              <X size={20} />
            </button>
          )}
        </div>

        {/* ── Crop UI ── */}
        {showCrop ? (
          <div className="flex flex-col flex-1 min-h-0">
            <p className="text-xs text-slate-500 px-5 pt-3 pb-1">
              Arraste para reposicionar e redimensione o quadrado para ajustar o recorte.
            </p>
            <div className="overflow-auto flex-1 flex items-center justify-center bg-slate-900 px-4 py-4">
              <ReactCrop
                crop={crop}
                onChange={(_, pct) => setCrop(pct)}
                onComplete={(_, pct) => setCompletedCrop(pct)}
                aspect={1}
                circularCrop={false}
                minWidth={20}
              >
                <img
                  ref={imgRef}
                  src={rawSrc}
                  alt="Recorte"
                  onLoad={onImageLoad}
                  style={{ maxHeight: '55vh', maxWidth: '100%', objectFit: 'contain' }}
                />
              </ReactCrop>
            </div>
            <div className="flex items-center justify-end gap-3 px-5 py-3 border-t border-slate-100 bg-white rounded-b-[28px]">
              <button onClick={handleCancelCrop} className="fin-btn-secondary">Cancelar</button>
              <button
                onClick={handleConfirmCrop}
                disabled={!completedCrop}
                className="inline-flex items-center gap-2 text-white text-sm font-semibold px-4 py-2 rounded-xl hover:opacity-90 disabled:opacity-50 transition-opacity"
                style={{ background: accent }}
              >
                <Check size={14} /> Confirmar recorte
              </button>
            </div>
          </div>
        ) : (
          <>
            <div className="px-6 pt-3 flex gap-2 border-b border-slate-100">
              {[
                { id: 'perfil', label: 'Perfil', icon: User },
                { id: 'senha', label: 'Alterar senha', icon: Lock },
              ].map(t => (
                <button key={t.id} onClick={() => { setTab(t.id); setError(''); setSuccess(''); }}
                  data-testid={`profile-tab-${t.id}`}
                  className={`px-3 py-2 text-xs font-bold uppercase tracking-widest border-b-2 transition-colors flex items-center gap-2 ${
                    tab === t.id ? 'border-current' : 'border-transparent text-slate-400 hover:text-slate-600'
                  }`}
                  style={tab === t.id ? { color: accent } : {}}>
                  <t.icon size={14} /> {t.label}
                </button>
              ))}
            </div>

            <div className="overflow-y-auto flex-1 min-h-0 p-5 space-y-4">
              {tab === 'perfil' && (
                <>
                  <div className="flex items-center gap-4">
                    {fotoPerfil ? (
                      <img src={fotoPerfil} alt="" className="w-20 h-20 rounded-2xl object-cover border border-slate-200" data-testid="profile-photo-preview" />
                    ) : (
                      <div className="w-20 h-20 rounded-2xl flex items-center justify-center text-white text-2xl font-bold"
                        style={{ background: accent }} data-testid="profile-photo-placeholder">
                        {initial}
                      </div>
                    )}
                    <div className="flex flex-col gap-2">
                      <button type="button" onClick={() => fileRef.current?.click()}
                        className="fin-btn-secondary text-xs" data-testid="profile-upload-btn">
                        <Upload size={14} /> {fotoPerfil ? 'Alterar foto' : 'Enviar foto'}
                      </button>
                      {fotoPerfil && (
                        <button type="button" onClick={() => { setFotoPerfil(''); setSuccess(''); fileRef.current && (fileRef.current.value = ''); }}
                          className="text-xs text-red-500 hover:underline inline-flex items-center gap-1" data-testid="profile-remove-photo">
                          <Trash2 size={12} /> Remover
                        </button>
                      )}
                      <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handlePhoto} />
                      <p className="text-xs text-slate-400 inline-flex items-center gap-1">
                        <Crop size={11} /> Recorte disponível após selecionar
                      </p>
                    </div>
                  </div>

                  {isSA && (
                    <div>
                      <label className="fin-label">Usuário (login) *</label>
                      <input type="text" value={username} onChange={e => { setUsername(e.target.value); if (success) setSuccess(''); }}
                        className="fin-input" data-testid="profile-username-input" />
                    </div>
                  )}

                  <div>
                    <label className="fin-label">Nome completo *</label>
                    <input type="text" value={nomeCompleto} onChange={e => { setNomeCompleto(e.target.value); if (success) setSuccess(''); }}
                      className="fin-input" placeholder="Como você quer ser chamado"
                      data-testid="profile-nome-input" />
                  </div>
                </>
              )}

              {tab === 'senha' && (
                <>
                  <div>
                    <label className="fin-label">Senha atual *</label>
                    <input type="password" value={senhaAtual} onChange={e => { setSenhaAtual(e.target.value); if (success) setSuccess(''); }}
                      className="fin-input" data-testid="profile-senha-atual" />
                  </div>
                  <div>
                    <label className="fin-label">Nova senha (mín. 8 caracteres) *</label>
                    <input type="password" value={senhaNova} onChange={e => { setSenhaNova(e.target.value); if (success) setSuccess(''); }}
                      className="fin-input" data-testid="profile-senha-nova" />
                  </div>
                  <div>
                    <label className="fin-label">Confirmar nova senha *</label>
                    <input type="password" value={senhaConfirm} onChange={e => { setSenhaConfirm(e.target.value); if (success) setSuccess(''); }}
                      className="fin-input" data-testid="profile-senha-confirm" />
                  </div>
                </>
              )}

              {/* Mensagem escrita em verde abaixo dos campos */}
              {success && (
                <div
                  className="flex items-center gap-3 p-3.5 bg-emerald-50 border border-emerald-300 rounded-2xl text-emerald-800 text-sm font-semibold shadow-sm"
                  data-testid="profile-modal-success"
                >
                  <div className="w-6 h-6 rounded-full bg-emerald-500 text-white flex items-center justify-center flex-shrink-0 shadow-sm">
                    <Check size={14} strokeWidth={3} />
                  </div>
                  <div className="flex flex-col">
                    <span className="text-emerald-900 font-bold leading-tight">{success}</span>
                    <span className="text-xs font-normal text-emerald-700">Alterações salvas com sucesso.</span>
                  </div>
                </div>
              )}

              {error && (
                <div
                  className="flex items-center gap-2.5 p-3.5 bg-red-50 border border-red-200 rounded-2xl text-red-700 text-sm font-medium"
                  data-testid="profile-modal-error"
                >
                  <AlertCircle size={18} className="text-red-500 flex-shrink-0" />
                  <span>{error}</span>
                </div>
              )}
            </div>

            <div className="flex items-center justify-between gap-3 px-6 py-3.5 border-t border-slate-100 bg-white rounded-b-[28px]">
              <div className="min-w-0 flex-1">
                {success && (
                  <span className="inline-flex items-center gap-1.5 text-xs font-bold text-emerald-600">
                    <CheckCircle2 size={15} className="text-emerald-500 flex-shrink-0" />
                    Salvo com sucesso!
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2.5 flex-shrink-0">
                <button onClick={onClose} className="fin-btn-secondary" disabled={loading} data-testid="profile-cancel-btn">Fechar</button>
                <button onClick={tab === 'perfil' ? saveProfile : savePassword} disabled={loading}
                  className="inline-flex items-center gap-2 text-white text-sm font-semibold px-4 py-2.5 rounded-xl transition-all hover:opacity-90 active:scale-[0.98] disabled:opacity-50"
                  style={{ background: accent }}
                  data-testid="profile-save-btn">
                  {loading ? 'Salvando...' : (tab === 'perfil' ? 'Salvar perfil' : 'Atualizar senha')}
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  ), document.body);
}
